/**
 * 用户权限解析器 - 核心层（框架无关）
 * 负责用户权限的解析、转换和权限树构建
 */

import { V6Permission } from '../permission/V6Permission'
import { PermissionParser } from '../permission/PermissionParser'
import { PermissionMatcher } from '../permission/PermissionMatcher'

export interface PermissionNode {
  id: string
  type: 'industry' | 'role' | 'module' | 'action'
  name: string
  code: string
  children: PermissionNode[]
  permissions: V6Permission[]
}

export interface PermissionTree {
  root: PermissionNode
  industries: PermissionNode[]
  roles: PermissionNode[]
  modules: PermissionNode[]
  actions: PermissionNode[]
}

export interface UserPermissionSummary {
  userId: string
  role: string
  industryCode: string
  totalPermissions: number
  availableIndustries: string[]
  availableModules: string[]
  availableActions: Record<string, string[]> // module -> actions
  permissionTree: PermissionTree
}

export interface PermissionResolutionOptions {
  expandWildcards?: boolean // 是否展开通配符权限
  groupBy?: 'industry' | 'role' | 'module' // 分组方式
  filterByIndustry?: string // 按行业过滤
  filterByModule?: string // 按模块过滤
  includeInherited?: boolean // 是否包含继承权限
}

/**
 * 用户权限解析器
 */
export class UserPermissionResolver {
  /**
   * 解析用户权限列表
   */
  static resolvePermissions(
    userPermissions: (V6Permission | string)[],
    userRole: string,
    userIndustryCode: string,
    options: PermissionResolutionOptions = {}
  ): UserPermissionSummary {
    const normalizedPermissions = userPermissions.map(perm => 
      typeof perm === 'string' ? V6Permission.fromString(perm) : perm
    )
    
    // 过滤和扩展权限
    const resolvedPermissions = this.processPermissions(
      normalizedPermissions,
      userRole,
      userIndustryCode,
      options
    )
    
    // 构建权限树
    const permissionTree = this.buildPermissionTree(resolvedPermissions, options)
    
    // 提取可用信息
    const availableIndustries = this.extractIndustries(resolvedPermissions)
    const availableModules = this.extractModules(resolvedPermissions)
    const availableActions = this.extractAvailableActions(resolvedPermissions)
    
    return {
      userId: '', // 需要在外部设置
      role: userRole,
      industryCode: userIndustryCode,
      totalPermissions: resolvedPermissions.length,
      availableIndustries,
      availableModules,
      availableActions,
      permissionTree
    }
  }

  /**
   * 检查用户是否有指定权限
   */
  static hasPermission(
    userPermissions: (V6Permission | string)[],
    userRole: string,
    userIndustryCode: string,
    requiredPermission: V6Permission | string,
    options: PermissionResolutionOptions = {}
  ): boolean {
    const normalizedUserPerms = userPermissions.map(perm => 
      typeof perm === 'string' ? V6Permission.fromString(perm) : perm
    )
    
    const resolvedPermissions = this.processPermissions(
      normalizedUserPerms,
      userRole,
      userIndustryCode,
      options
    )
    
    const normalizedRequired = typeof requiredPermission === 'string'
      ? V6Permission.fromString(requiredPermission)
      : requiredPermission
    
    return PermissionMatcher.canPerformAction(
      resolvedPermissions,
      userRole,
      userIndustryCode,
      normalizedRequired.module,
      normalizedRequired.action
    )
  }

  /**
   * 获取用户在某模块的可用操作
   */
  static getAvailableActions(
    userPermissions: (V6Permission | string)[],
    userRole: string,
    userIndustryCode: string,
    module: string
  ): string[] {
    return PermissionMatcher.getAvailableActions(
      userPermissions,
      userRole,
      userIndustryCode,
      module
    )
  }

  /**
   * 获取用户可访问的行业列表
   */
  static getAvailableIndustries(
    userPermissions: (V6Permission | string)[],
    userRole: string
  ): string[] {
    const normalizedPermissions = userPermissions.map(perm => 
      typeof perm === 'string' ? V6Permission.fromString(perm) : perm
    )
    
    const industries = new Set<string>()
    
    normalizedPermissions.forEach(perm => {
      if (perm.role === userRole && perm.industryCode !== '*') {
        industries.add(perm.industryCode)
      }
    })
    
    // 如果用户有通配符行业权限，返回所有行业
    if (normalizedPermissions.some(perm => 
      perm.role === userRole && perm.industryCode === '*'
    )) {
      return ['AGR001', 'AUT001', 'EST001', 'EDU001']
    }
    
    return Array.from(industries)
  }

  /**
   * 获取用户权限的树形结构
   */
  static getPermissionTree(
    userPermissions: (V6Permission | string)[],
    userRole: string,
    userIndustryCode: string,
    options: PermissionResolutionOptions = {}
  ): PermissionTree {
    const normalizedPermissions = userPermissions.map(perm => 
      typeof perm === 'string' ? V6Permission.fromString(perm) : perm
    )
    
    const resolvedPermissions = this.processPermissions(
      normalizedPermissions,
      userRole,
      userIndustryCode,
      options
    )
    
    return this.buildPermissionTree(resolvedPermissions, options)
  }

  /**
   * 获取权限的继承关系
   */
  static getPermissionInheritance(
    permission: V6Permission | string
  ): V6Permission[] {
    const perm = typeof permission === 'string' 
      ? V6Permission.fromString(permission)
      : permission
    
    const inherited: V6Permission[] = []
    
    // 行业通配符继承
    if (perm.industryCode !== '*') {
      inherited.push(
        new V6Permission('*', perm.role, perm.module, perm.action)
      )
    }
    
    // 模块通配符继承
    if (perm.module !== '*') {
      inherited.push(
        new V6Permission(perm.industryCode, perm.role, '*', perm.action)
      )
    }
    
    // 操作通配符继承
    if (perm.action !== '*') {
      inherited.push(
        new V6Permission(perm.industryCode, perm.role, perm.module, '*')
      )
    }
    
    return inherited
  }

  /**
   * 处理权限列表（过滤、扩展等）
   */
  private static processPermissions(
    permissions: V6Permission[],
    userRole: string,
    userIndustryCode: string,
    options: PermissionResolutionOptions
  ): V6Permission[] {
    let result = [...permissions]
    
    // 过滤用户角色
    result = result.filter(perm => perm.role === userRole)
    
    // 过滤用户行业
    result = result.filter(perm => 
      perm.industryCode === '*' || perm.industryCode === userIndustryCode
    )
    
    // 按选项过滤
    if (options.filterByIndustry) {
      result = result.filter(perm => 
        perm.industryCode === '*' || perm.industryCode === options.filterByIndustry
      )
    }
    
    if (options.filterByModule) {
      result = result.filter(perm => 
        perm.module === '*' || perm.module === options.filterByModule
      )
    }
    
    // 展开通配符权限
    if (options.expandWildcards) {
      result = this.expandWildcardPermissions(result)
    }
    
    // 包含继承权限
    if (options.includeInherited) {
      const inherited: V6Permission[] = []
      result.forEach(perm => {
        inherited.push(...this.getPermissionInheritance(perm))
      })
      result = [...result, ...inherited]
    }
    
    // 去重
    result = this.removeDuplicatePermissions(result)
    
    return result
  }

  /**
   * 展开通配符权限
   */
  private static expandWildcardPermissions(permissions: V6Permission[]): V6Permission[] {
    const expanded: V6Permission[] = []
    
    const allIndustries = ['AGR001', 'AUT001', 'EST001', 'EDU001']
    const allModules = ['content', 'template', 'task', 'quality', 'distribution', 'analysis', 'statistics', 'report', 'dashboard']
    const allActions = ['view', 'create', 'edit', 'delete', 'publish', 'review', 'export']
    
    permissions.forEach(perm => {
      const industries = perm.industryCode === '*' ? allIndustries : [perm.industryCode]
      const modules = perm.module === '*' ? allModules : [perm.module]
      const actions = perm.action === '*' ? allActions : [perm.action]
      
      industries.forEach(industry => {
        modules.forEach(module => {
          actions.forEach(action => {
            expanded.push(new V6Permission(industry, perm.role, module, action))
          })
        })
      })
    })
    
    return expanded
  }

  /**
   * 构建权限树
   */
  private static buildPermissionTree(
    permissions: V6Permission[],
    options: PermissionResolutionOptions
  ): PermissionTree {
    const groupBy = options.groupBy || 'industry'
    
    let rootNode: PermissionNode
    const industryNodes: PermissionNode[] = []
    const roleNodes: PermissionNode[] = []
    const moduleNodes: PermissionNode[] = []
    const actionNodes: PermissionNode[] = []
    
    switch (groupBy) {
      case 'industry':
        rootNode = this.buildTreeByIndustry(permissions)
        break
      case 'role':
        rootNode = this.buildTreeByRole(permissions)
        break
      case 'module':
        rootNode = this.buildTreeByModule(permissions)
        break
      default:
        rootNode = this.buildTreeByIndustry(permissions)
    }
    
    // 提取各个层级的节点
    this.traverseTree(rootNode, (node, depth) => {
      switch (node.type) {
        case 'industry':
          industryNodes.push(node)
          break
        case 'role':
          roleNodes.push(node)
          break
        case 'module':
          moduleNodes.push(node)
          break
        case 'action':
          actionNodes.push(node)
          break
      }
    })
    
    return {
      root: rootNode,
      industries: industryNodes,
      roles: roleNodes,
      modules: moduleNodes,
      actions: actionNodes
    }
  }

  /**
   * 按行业构建树
   */
  private static buildTreeByIndustry(permissions: V6Permission[]): PermissionNode {
    const root: PermissionNode = {
      id: 'root',
      type: 'industry',
      name: '所有行业',
      code: '*',
      children: [],
      permissions: []
    }
    
    const industryMap = new Map<string, PermissionNode>()
    
    permissions.forEach(perm => {
      const industryCode = perm.industryCode
      
      if (!industryMap.has(industryCode)) {
        const industryNode: PermissionNode = {
          id: `industry_${industryCode}`,
          type: 'industry',
          name: this.getIndustryName(industryCode),
          code: industryCode,
          children: [],
          permissions: []
        }
        industryMap.set(industryCode, industryNode)
        root.children.push(industryNode)
      }
      
      const industryNode = industryMap.get(industryCode)!
      industryNode.permissions.push(perm)
      
      // 添加角色节点
      const roleNodeId = `role_${perm.role}`
      let roleNode = industryNode.children.find(n => n.id === roleNodeId)
      
      if (!roleNode) {
        roleNode = {
          id: roleNodeId,
          type: 'role',
          name: this.getRoleName(perm.role),
          code: perm.role,
          children: [],
          permissions: []
        }
        industryNode.children.push(roleNode)
      }
      
      roleNode.permissions.push(perm)
      
      // 添加模块节点
      const moduleNodeId = `module_${perm.module}`
      let moduleNode = roleNode.children.find(n => n.id === moduleNodeId)
      
      if (!moduleNode) {
        moduleNode = {
          id: moduleNodeId,
          type: 'module',
          name: this.getModuleName(perm.module),
          code: perm.module,
          children: [],
          permissions: []
        }
        roleNode.children.push(moduleNode)
      }
      
      moduleNode.permissions.push(perm)
      
      // 添加操作节点
      const actionNodeId = `action_${perm.action}`
      let actionNode = moduleNode.children.find(n => n.id === actionNodeId)
      
      if (!actionNode) {
        actionNode = {
          id: actionNodeId,
          type: 'action',
          name: this.getActionName(perm.action),
          code: perm.action,
          children: [],
          permissions: []
        }
        moduleNode.children.push(actionNode)
      }
      
      actionNode.permissions.push(perm)
    })
    
    return root
  }

  /**
   * 按角色构建树
   */
  private static buildTreeByRole(permissions: V6Permission[]): PermissionNode {
    const root: PermissionNode = {
      id: 'root',
      type: 'role',
      name: '所有角色',
      code: '*',
      children: [],
      permissions: []
    }
    
    const roleMap = new Map<string, PermissionNode>()
    
    permissions.forEach(perm => {
      const roleCode = perm.role
      
      if (!roleMap.has(roleCode)) {
        const roleNode: PermissionNode = {
          id: `role_${roleCode}`,
          type: 'role',
          name: this.getRoleName(roleCode),
          code: roleCode,
          children: [],
          permissions: []
        }
        roleMap.set(roleCode, roleNode)
        root.children.push(roleNode)
      }
      
      const roleNode = roleMap.get(roleCode)!
      roleNode.permissions.push(perm)
      
      // 后续层级类似...
    })
    
    return root
  }

  /**
   * 按模块构建树
   */
  private static buildTreeByModule(permissions: V6Permission[]): PermissionNode {
    const root: PermissionNode = {
      id: 'root',
      type: 'module',
      name: '所有模块',
      code: '*',
      children: [],
      permissions: []
    }
    
    const moduleMap = new Map<string, PermissionNode>()
    
    permissions.forEach(perm => {
      const moduleCode = perm.module
      
      if (!moduleMap.has(moduleCode)) {
        const moduleNode: PermissionNode = {
          id: `module_${moduleCode}`,
          type: 'module',
          name: this.getModuleName(moduleCode),
          code: moduleCode,
          children: [],
          permissions: []
        }
        moduleMap.set(moduleCode, moduleNode)
        root.children.push(moduleNode)
      }
      
      const moduleNode = moduleMap.get(moduleCode)!
      moduleNode.permissions.push(perm)
      
      // 后续层级类似...
    })
    
    return root
  }

  /**
   * 遍历权限树
   */
  private static traverseTree(
    node: PermissionNode,
    callback: (node: PermissionNode, depth: number) => void,
    depth = 0
  ): void {
    callback(node, depth)
    
    node.children.forEach(child => {
      this.traverseTree(child, callback, depth + 1)
    })
  }

  /**
   * 提取行业列表
   */
  private static extractIndustries(permissions: V6Permission[]): string[] {
    const industries = new Set<string>()
    
    permissions.forEach(perm => {
      if (perm.industryCode !== '*') {
        industries.add(perm.industryCode)
      }
    })
    
    return Array.from(industries)
  }

  /**
   * 提取模块列表
   */
  private static extractModules(permissions: V6Permission[]): string[] {
    const modules = new Set<string>()
    
    permissions.forEach(perm => {
      if (perm.module !== '*') {
        modules.add(perm.module)
      }
    })
    
    return Array.from(modules)
  }

  /**
   * 提取可用操作
   */
  private static extractAvailableActions(permissions: V6Permission[]): Record<string, string[]> {
    const actionsByModule: Record<string, Set<string>> = {}
    
    permissions.forEach(perm => {
      if (perm.action !== '*') {
        if (!actionsByModule[perm.module]) {
          actionsByModule[perm.module] = new Set<string>()
        }
        actionsByModule[perm.module].add(perm.action)
      }
    })
    
    const result: Record<string, string[]> = {}
    Object.keys(actionsByModule).forEach(module => {
      result[module] = Array.from(actionsByModule[module])
    })
    
    return result
  }

  /**
   * 移除重复权限
   */
  private static removeDuplicatePermissions(permissions: V6Permission[]): V6Permission[] {
    const uniqueMap = new Map<string, V6Permission>()
    
    permissions.forEach(perm => {
      uniqueMap.set(perm.toString(), perm)
    })
    
    return Array.from(uniqueMap.values())
  }

  /**
   * 获取行业名称
   */
  private static getIndustryName(code: string): string {
    const map: Record<string, string> = {
      'AGR001': '农业',
      'AUT001': '汽车',
      'EST001': '房地产',
      'EDU001': '教育',
      '*': '所有行业'
    }
    return map[code] || code
  }

  /**
   * 获取角色名称
   */
  private static getRoleName(code: string): string {
    const map: Record<string, string> = {
      'platform_admin': '平台管理员',
      'platform_operator': '内容运营',
      'platform_expert': '平台专家',
      'platform_business': '企业主投喂GEO后台',
      'vendor_owner': '代理商主',
      'vendor_operator': '运营主管',
      'vendor_partner': '销售伙伴',
      'vendor_expert': '代理商专家',
      'vendor_staff': '运营专员',
      'business_owner': '业务主',
      'business_manager': '运营经理',
      'business_member': '企业成员'
    }
    return map[code] || code
  }

  /**
   * 获取模块名称
   */
  private static getModuleName(code: string): string {
    const map: Record<string, string> = {
      'content': '内容管理',
      'template': '模板管理',
      'task': '任务管理',
      'quality': '质量管理',
      'distribution': '分发管理',
      'analysis': '分析管理',
      'statistics': '统计管理',
      'report': '报告管理',
      'dashboard': '仪表板',
      '*': '所有模块'
    }
    return map[code] || code
  }

  /**
   * 获取操作名称
   */
  private static getActionName(code: string): string {
    const map: Record<string, string> = {
      'view': '查看',
      'create': '创建',
      'edit': '编辑',
      'delete': '删除',
      'publish': '发布',
      'review': '审核',
      'export': '导出',
      '*': '所有操作'
    }
    return map[code] || code
  }
}